import { Grid, Stack, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';

import { FormProvider, GlassEffectDialog, Iconify, Scrollbar } from '@app/components';
import {
  selectBoardDetailsState,
  selectGetBackgroundState,
  selectUpdateBoardState,
} from '../selector';
import { useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { getBackground, getBoardDetails, updateBoard } from '../slice';
import { IMAGES } from './images';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  iconName: string;
  setSelectedManuallyImage?: (image?: string) => void;
}

const ChangeBackground = ({ open, onClose, iconName, setSelectedManuallyImage }: Props) => {
  const methods = useForm();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });
  const { data: background } = useRequestState({
    stateSelector: selectGetBackgroundState,
    errorShown: true,
  });
  const imagesToShow = Object.values(IMAGES).slice(0, 9);

  useEffect(() => {
    dispatch(getBackground());
  }, [dispatch]);

  const [selectedImage, setSelectedImage] = useState<string>(data?.image || background?.[0]?.image);
  const [image, setImage] = useState<string | undefined | any>();

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];

    if (file) {
      setSelectedImage('');
      methods.setValue('image', file, { shouldValidate: true });
      setImage(file);
    }
  };

  const handleColorSelect = (image: string) => {
    setImage(undefined);
    setSelectedImage(image);
  };
  const { loading } = useRequestState({
    stateSelector: selectUpdateBoardState,
    onSuccess: onClose,
    errorShown: true,
  });
  const onSubmit = () => {
    const imageToSubmit = selectedImage || image;
    dispatch(
      updateBoard({
        boardId: data?.id,
        image: imageToSubmit,
        boardName: data?.name,
        description: data?.description,
      })
    );
    dispatch(getBoardDetails(data?.id));
  };
  return (
    <>
      <FormProvider methods={methods}>
        <GlassEffectDialog
          open={open}
          onClose={onClose}
          title={'Change Background'}
          backgroundImage={data?.image}
          boardIcon={iconName}
          height="100vh"
        >
          <Stack sx={{ width: '350px' }} mt={-2}>
            <Stack
              fontSize={18}
              fontWeight={700}
              sx={{
                color: theme.palette.taskboard.raisinBlack,
              }}
            >
              {Strings.taskBoard.recentBackground}
            </Stack>
            <Stack my={2} justifyContent="center" direction={'row'} spacing={2} ml={6} mr={6}>
              <Stack
                sx={{
                  height: '150px',
                  width: '250px',
                  backgroundImage: image
                    ? `url(${URL.createObjectURL(image)})`
                    : `url(${selectedImage})`,
                  backgroundSize: 'cover',
                  borderRadius: '2px',
                  alignItems: 'center',
                }}
              />
            </Stack>
          </Stack>
          <Stack
            fontSize={18}
            fontWeight={700}
            sx={{
              color: theme.palette.taskboard.raisinBlack,
            }}
          >
            {Strings.taskBoard.background}
          </Stack>
          <Stack direction={'row'} my={2}>
            <Stack justifyContent="center" direction={'row'} spacing={1} sx={{ width: '350px' }}>
              <Scrollbar sx={{ height: '130px' }}>
                <Grid container spacing={1}>
                  {imagesToShow?.map((image: string, index: number) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Stack
                        onClick={() => {
                          handleColorSelect(image);
                        }}
                        sx={{
                          height: '60px',
                          width: '100%',
                          backgroundImage: `url(${image})`,
                          backgroundSize: 'cover',
                          borderRadius: '2px',
                          alignItems: 'center',
                        }}
                      >
                        {selectedImage === image && (
                          <Iconify
                            icon={'mdi:check'}
                            width={30}
                            sx={{
                              color: theme.palette.common.white,
                              justifyContent: 'center',
                              alignItems: 'center',
                              mt: 2,
                            }}
                          />
                        )}
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Scrollbar>
            </Stack>
          </Stack>
          <Stack justifyContent={'space-between'} direction={'row'}>
            <Stack>
              <input type="file" id="file" hidden onChange={handleFileChange} />
              <label htmlFor="file">
                <span
                  style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    backgroundColor: theme.palette.grey[300],
                    color: 'black',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {Strings.taskBoard.chooseFromGallery}
                </span>
              </label>
            </Stack>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={loading}
              onClick={methods.handleSubmit(onSubmit)}
              sx={{
                bgcolor: theme.palette.taskboard.raisinBlack,
                width: '20%',
                '&:hover': {
                  bgcolor: theme.palette.taskboard.raisinBlack,
                },
              }}
            >
              {Strings.button.save}
            </LoadingButton>
          </Stack>
        </GlassEffectDialog>
      </FormProvider>
    </>
  );
};

export { ChangeBackground };
